import { AxiosError } from 'axios';
import { APIGatewayResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { useContext, useContextSelector } from 'use-context-selector';
import { BeneficiaryListResponse } from '../../domain/Beneficiary/entities/BeneficiariesResponseDTO';
import { BeneficiariesContext } from './BeneficiariesContext';

const useBeneficiariesState = () => {
  const context = useContext(BeneficiariesContext);

  if (context === undefined) {
    throw new Error('useBeneficiariesState must be used within TransactionsProvider');
  }

  return context;
};

const useBeneficiaries = (limit?: number) => {
  const context = useContextSelector(BeneficiariesContext, state => {
    if (limit && state.beneficiaries) {
      return state.beneficiaries?.slice(0, limit);
    }
    return state?.beneficiaries ?? [];
  });

  if (context === undefined) {
    throw new Error('useBeneficiaries must be used within BeneficiariesProvider');
  }

  return context;
};

const useBeneficiariesRefetch = (limit?: number) => {
  const context = useContextSelector(BeneficiariesContext, state => state.beneficiariesRefetch);

  if (context === undefined) {
    throw new Error('useBeneficiariesRefetch must be used within BeneficiariesProvider');
  }

  return context;
};

const useBeneficiariesLoadingState = (loadingProperty: 'isLoadingBeneficiaries' | 'isLoadingDeleteBeneficiary') => {
  const context = useContextSelector(BeneficiariesContext, state => state.loadingState);

  if (context === undefined) {
    throw new Error('useBeneficiariesLoadingState must be used within TransactionsProvider');
  }

  return context[loadingProperty];
};

const useBeneficiariesErrorState = (
  errorProperty: 'beneficiariesDataError' | 'deleteBeneficiaryError',
): AxiosError<APIGatewayResponse<BeneficiaryListResponse | any>, any> | undefined => {
  const context = useContextSelector(BeneficiariesContext, state => state.errorState);

  if (context === undefined) {
    throw new Error('useBeneficiariesLoadingState must be used within TransactionListProvider');
  }

  return context[errorProperty];
};

const useSelectedBeneficiary = () => {
  const context = useContextSelector(BeneficiariesContext, state => state.selectedBeneficiary);

  if (context === undefined) {
    throw new Error('useSelectedBeneficiary must be used within BeneficiariesProvider');
  }

  return context;
};

const useOnBeneficiaryAction = () => {
  const context = useContextSelector(BeneficiariesContext, state => state.onBeneficiaryAction);

  if (context === undefined) {
    throw new Error('useOnBeneficiaryAction must be used within BeneficiariesProvider');
  }

  return context;
};

const useBeneficiarySelectedAction = () => {
  const context = useContextSelector(BeneficiariesContext, state => state.action);

  if (context === undefined) {
    throw new Error('useOnBeneficiaryAction must be used within BeneficiariesProvider');
  }

  return context;
};

const useOnDeleteBeneficiary = () => {
  const context = useContextSelector(BeneficiariesContext, state => state.onDeleteBeneficiary);

  if (context === undefined) {
    throw new Error('useOnEditBeneficiary must be used within BeneficiariesProvider');
  }

  return context;
};

const useOnResetSelectedBeneficiary = () => {
  const context = useContextSelector(BeneficiariesContext, state => state.onResetSelectedBeneficiary);

  if (context === undefined) {
    throw new Error('useOnEditBeneficiary must be used within BeneficiariesProvider');
  }

  return context;
};

const useOnBeneficiariesListFiltersChange = () => {
  const context = useContextSelector(BeneficiariesContext, state => state.onBeneficiariesListFiltersChange);

  if (context === undefined) {
    throw new Error('useOnBeneficiariesListFiltersChange must be used within BeneficiariesProvider');
  }

  return context;
};

const useBeneficiariesListFilters = () => {
  const context = useContextSelector(BeneficiariesContext, state => state.filters);

  if (context === undefined) {
    throw new Error('useBeneficiariesListFilters must be used within BeneficiariesProvider');
  }

  return context;
};

const useTotalBeneficiaries = () => {
  const context = useContextSelector(BeneficiariesContext, state => state.totalBeneficiaries);

  if (context === undefined) {
    throw new Error('useTotalBeneficiaries must be used within BeneficiariesProvider');
  }

  return context;
};


const useDeleteBeneficiaryData = () => {
  const context = useContextSelector(BeneficiariesContext, state => state.deleteBeneficiaryData);

  return context;
};

export {
  useBeneficiaries,
  useOnDeleteBeneficiary,
  useBeneficiariesState,
  useSelectedBeneficiary,
  useOnBeneficiaryAction,
  useBeneficiariesErrorState,
  useBeneficiarySelectedAction,
  useBeneficiariesLoadingState,
  useOnResetSelectedBeneficiary,
  useDeleteBeneficiaryData,
  useBeneficiariesRefetch,
  useOnBeneficiariesListFiltersChange,
  useBeneficiariesListFilters,
  useTotalBeneficiaries
};
